import axios from "axios";
import snackbarService from "./snackbarService";
import { auth } from "../firebase";

// ─── Token-refresh interceptor ───────────────────────────────────────────────
// When any request returns 401, silently refresh the Firebase ID token and
// retry once.  Concurrent requests that arrive while a refresh is in progress
// are queued and replayed with the new token.
//
// After long inactivity the browser may throttle Firebase's background refresh
// timer, leaving the stored token stale.  We handle this by:
//   1. Waiting for auth.authStateReady() so Firebase can restore its state
//      from IndexedDB before we give up (covers the resume-from-suspend gap).
//   2. Redirecting to /login if a refresh still cannot be obtained — this is
//      always better UX than a blank page.
let _isRefreshing = false;
let _refreshQueue = [];

const _processQueue = (err, token = null) => {
  _refreshQueue.forEach(({ resolve, reject }) =>
    err ? reject(err) : resolve(token)
  );
  _refreshQueue = [];
};

const _redirectToLogin = () => {
  localStorage.removeItem("backend_token");
  // Only redirect if we're not already on an auth page to avoid loops.
  if (!window.location.pathname.startsWith("/login") &&
      !window.location.pathname.startsWith("/register")) {
    window.location.href = "/login";
  }
};

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config;
    if (error.response?.status === 401 && !original._retry) {
      if (_isRefreshing) {
        return new Promise((resolve, reject) => {
          _refreshQueue.push({ resolve, reject });
        }).then((token) => {
          original.headers.Authorization = `Bearer ${token}`;
          return axios(original);
        }).catch((err) => {
          _redirectToLogin();
          return Promise.reject(err);
        });
      }
      original._retry = true;
      _isRefreshing = true;
      try {
        // Wait for Firebase to finish restoring auth state from IndexedDB.
        // This is a no-op if Firebase is already ready (the common case).
        await auth.authStateReady();

        const currentUser = auth.currentUser;
        if (!currentUser) {
          // No authenticated user even after Firebase is ready → session gone.
          _processQueue(null, null);
          _redirectToLogin();
          return Promise.reject(new Error("Session expired. Please log in again."));
        }

        const token = await currentUser.getIdToken(true); // force refresh
        localStorage.setItem("backend_token", token);
        _processQueue(null, token);
        original.headers.Authorization = `Bearer ${token}`;
        return axios(original);
      } catch (refreshError) {
        _processQueue(refreshError, null);
        // Token refresh failed despite Firebase being ready — redirect to login.
        _redirectToLogin();
        return Promise.reject(refreshError);
      } finally {
        _isRefreshing = false;
      }
    }
    return Promise.reject(error);
  }
);

/**
 *
 * @param {string} url - API endpoint
 * @param {Object|FormData} data - payload
 * @param {string} success - success toast message
 * @param {string} errormessage - fallback error toast
 * @param {'json' | 'form'} dataType - determines content-type
 */

const postData = async (
  url,
  data,
  success,
  errormessage,
  dataType = "json" // 'json' or 'form'
) => {
  const new_url = `${process.env.REACT_APP_API_URL}/${url}`;

  // Determine if the payload is FormData
  const isFormData = data instanceof FormData;

  const config = {
    headers: {
      Authorization: `Bearer ${localStorage.getItem("backend_token")}`,
      Accept: "application/json",
      // Only set Content-Type to application/json for JSON data
      // For FormData, let the browser set it (with boundary)
      ...(dataType === "json" &&
        !isFormData && { "Content-Type": "application/json" }),
    },
  };

  try {
    // If it's JSON data, stringify it. Otherwise, pass FormData directly.
    const payload =
      dataType === "json" && !isFormData ? JSON.stringify(data) : data;
    const response = await axios.post(new_url, payload, config);

    if (success) {
      snackbarService.success(success);
    }
    return response.data;
  } catch (error) {
    console.error("Error:", error.response || error.message);
    snackbarService.error(error.response?.data?.message || errormessage);
    throw error;
  }
};

const postPayment = async (url, data) => {
  const new_url = `${process.env.REACT_APP_API_URL}/${url}`;
  const config = {
    headers: {
      Authorization: `Bearer ${localStorage.getItem("backend_token")}`,
      Accept: "application/json",
      "Content-Type": "application/json",
    },
  };

  try {
    const payload = JSON.stringify(data);
    const response = await axios.post(new_url, payload, config);
    console.log(response);
    return response.data;
  } catch (error) {
    throw error;
  }
};

const patchData = async (
  url,
  data,
  success,
  errormessage,
  dataType = "json"
) => {
  const new_url = `${process.env.REACT_APP_API_URL}/${url}`;

  const config = {
    headers: {
      Authorization: `Bearer ${localStorage.getItem("backend_token")}`,
      ...(dataType === "json"
        ? {
            "Content-Type": "application/json",
            Accept: "application/json",
          }
        : {}), // Let browser handle headers for FormData
    },
  };

  try {
    const payload = dataType === "json" ? JSON.stringify(data) : data;
    const response = await axios.patch(new_url, payload, config);
    if (success) {
      snackbarService.success(success);
    }

    return response.data;
  } catch (error) {
    console.error("Error:", error.response || error.message);
    snackbarService.error(error.response?.data?.message || errormessage);
  }
};

const putData = async (
  url,
  data,
  success,
  errormessage,
  dataType = "json"
) => {
  const new_url = `${process.env.REACT_APP_API_URL}/${url}`;

  const config = {
    headers: {
      Authorization: `Bearer ${localStorage.getItem("backend_token")}`,
      ...(dataType === "json"
        ? {
            "Content-Type": "application/json",
            Accept: "application/json",
          }
        : {}),
    },
  };

  try {
    const payload = dataType === "json" ? JSON.stringify(data) : data;
    const response = await axios.put(new_url, payload, config);
    if (success) {
      snackbarService.success(success);
    }

    return response.data;
  } catch (error) {
    console.error("Error:", error.response || error.message);
    snackbarService.error(error.response?.data?.message || errormessage);
    throw error;
  }
};

const deleteData = async (url, success, errormessage) => {
  const new_url = `${process.env.REACT_APP_API_URL}/${url}`;

  const config = {
    headers: {
      Authorization: `Bearer ${localStorage.getItem("backend_token")}`,
      Accept: "application/json",
    },
  };

  try {
    const response = await axios.delete(new_url, config);
    if (success) {
      snackbarService.success(success);
    }

    return response.data;
  } catch (error) {
    console.error("Error:", error.response || error.message);
    snackbarService.error(error.response?.data?.message || errormessage);
    throw error;
  }
};

const getData = async (url, errorMessage, page = 1, extraParams = {}) => {
  const fullUrl = `${process.env.REACT_APP_API_URL}/${url}`;
  const token = localStorage.getItem("backend_token");

  const config = {
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    params: {
      page, // this will append ?page=... to the URL
      ...extraParams,
    },
  };

  try {
    const response = await axios.get(fullUrl, config);
    console.log(response);
    return response.data;
  } catch (error) {
    // 401s are handled by the interceptor above (refresh + redirect to /login).
    // Only show an error snackbar for non-auth failures.
    const isAuthError = error.response?.status === 401 || error.response?.status === 403;
    if (!isAuthError && errorMessage) {
      snackbarService.error(error.response?.data?.detail || errorMessage);
    }
    console.error("Error fetching data:", error);
    return null;
  }
};

const searchData = async (
  endpoint,
  searchQuery,
  errorMessage = "No results found"
) => {
  const fullUrl = `${process.env.REACT_APP_API_URL}/${endpoint}`;

  const config = {
    headers: {
      Authorization: `Bearer ${localStorage.getItem("backend_token")}`,
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    params: {
      q: searchQuery,
    },
  };

  try {
    const response = await axios.get(fullUrl, config);
    console.log("Search Response:", response);
    return response.data?.results || [];
  } catch (error) {
    snackbarService.error(error.message || errorMessage);
    console.error("Search error:", error);
    return [];
  }
};

export default searchData;

export { getData, postData, patchData, putData, deleteData, searchData, postPayment };
